'use client';

import React, { useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

// EWM-314 — global reCAPTCHA Enterprise script provider. Renders children
// BARE when NEXT_PUBLIC_RECAPTCHA_SITE_KEY is unset (keyless dev / preview
// envs): no script tag, no provider — useRecaptcha degrades to undefined
// tokens and the BFF guard skips verification. The site key is
// public-by-design (rendered into the page; same convention as the GTM ids).
// NOTE: the key is read inside the component (not module scope) so specs can
// vary process.env per test.

const RecaptchaProvider = ({ children }: React.PropsWithChildren) => {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (!siteKey) {
      console.warn('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is unset — reCAPTCHA disabled (degraded mode).');
    }
  }, [siteKey]);

  if (!siteKey) {
    return <>{children}</>;
  }

  // EWM-839 — scriptProps tuning. The library inserts the reCAPTCHA <script>
  // via document.createElement/appendChild inside a useEffect (i.e. after
  // mount/commit), so it is never parser-blocking and was never competing
  // with first paint — async/defer don't change that. What `async: true`
  // does do: without it, the library defaults to async: false, which puts
  // the dynamically-created script into the browser's in-order execution
  // queue alongside other dynamically-inserted scripts, so it can still be
  // forced to wait on them; async: true releases that ordering constraint.
  // `defer` is inert here — per the HTML spec it only affects
  // parser-inserted <script> elements, not ones created via
  // document.createElement — it's passed only for API completeness/clarity.
  // reCAPTCHA stays site-wide (not lazy-loaded on just auth/checkout pages)
  // because score-based reCAPTCHA needs app-wide context to build an
  // accurate risk model.
  return (
    <GoogleReCaptchaProvider useEnterprise reCaptchaKey={siteKey} scriptProps={{ async: true, defer: true }}>
      {children}
    </GoogleReCaptchaProvider>
  );
};

export default RecaptchaProvider;
